// DNA v6 "white blueprint" — the ONE shared token file for every Agentic OS screen.
// CommonJS so tailwind.config.js can require it; React imports it as a default export.

const colors = {
  canvas: "#FFFFFF",
  "card-end": "#FCFCFB",
  wash: "#F9F9F9",
  ink: "#171717",
  "ink-2": "#5D5D5D",
  "ink-3": "#7E7E7E",
  "ink-4": "#A0A0A0",
  border: "#E5E7EB",
  "border-strong": "#D1D5DB",
  red: "#C22B10",
  amber: "#B45309",
  green: "#15803D",
  gold: "#FFC838",
  "gold-tint": "rgba(255, 200, 56, 0.22)",
  bronze: "rgba(196, 148, 28, 0.62)",
  "bronze-strong": "rgba(196, 148, 28, 0.78)",
};

const effects = {
  cardGradient: "linear-gradient(180deg, #FFFFFF 0%, #FCFCFB 100%)",
  cardShadow: "0 8px 18px rgba(0,0,0,.04), 0 33px 33px rgba(0,0,0,.03)",
  engrave: "inset 0 1px 0 rgba(255,255,255,0.95)",
  gridDot: "rgba(23,23,23,0.05)",
  gridSize: "22px",
  pastTint: 0.35,
};

const radius = { card: "20px", chip: "10px", control: "10px", segment: "2px" };

const track = { length: 16, height: "6px", gap: "3px" };

const fonts = {
  inter: ['"Inter"', "ui-sans-serif", "sans-serif"],
  mono: ['"IBM Plex Mono"', "ui-monospace", "monospace"],
};

const swatches = [
  { group: "Canvas", token: "canvas", role: "Canvas and card start" },
  { group: "Canvas", token: "card-end", role: "Card gradient end (whisper-warm)" },
  { group: "Canvas", token: "wash", role: "Panel wash" },
  { group: "Ink", token: "ink", role: "Primary ink — numbers, titles, clean segments" },
  { group: "Ink", token: "ink-2", role: "Secondary ink — state lines" },
  { group: "Ink", token: "ink-3", role: "Tertiary ink — meta, quiet word" },
  { group: "Ink", token: "ink-4", role: "Faint — small uppercase labels" },
  { group: "Lines", token: "border", role: "Hairline border" },
  { group: "Lines", token: "border-strong", role: "Strong border, missed segments" },
  { group: "Status", token: "green", role: "Healthy — words, tiny marks, segments" },
  { group: "Status", token: "amber", role: "Flagged — words, tiny marks, segments" },
  { group: "Status", token: "red", role: "Broken — words, tiny marks, segments" },
  { group: "Accent", token: "gold", role: "Waiting-on-you chip hue (used only as the tint below)" },
  { group: "Accent", token: "gold-tint", role: "Waiting-on-you chip fill — gold at 0.22" },
  { group: "Accent", token: "bronze", role: "Waiting-on-you chip keyline" },
  { group: "Accent", token: "bronze-strong", role: "Waiting-on-you chip bottom edge" },
];

const typeScale = [
  { name: "Summary strip", family: "inter", size: "1.75rem", weight: 600, tracking: "-0.02em", case: "Sentence", sample: "1 broken · 1 alert firing" },
  { name: "Big number", family: "inter", size: "2.875rem", weight: 600, tracking: "-0.035em", case: "Tabular numerals", sample: "48" },
  { name: "Title", family: "inter", size: "0.9375rem", weight: 600, tracking: "-0.01em", case: "Sentence", sample: "Inbox scan" },
  { name: "Body", family: "inter", size: "0.875rem", weight: 400, tracking: "0", case: "Sentence", sample: "All 48 page checks passed." },
  { name: "State word", family: "inter", size: "0.6875rem", weight: 600, tracking: "0.08em", case: "UPPERCASE", sample: "ALERT FIRED" },
  { name: "Label", family: "inter", size: "0.6875rem", weight: 500, tracking: "0.08em", case: "UPPERCASE", sample: "PAGES PASSING" },
  { name: "Mono meta", family: "mono", size: "0.6875rem", weight: 400, tracking: "0", case: "lowercase", sample: "checked 10:14 AM · 14 / 16 clean" },
];

const motion = [
  { name: "Loops", rule: "None. Nothing loops anywhere." },
  { name: "Numbers", rule: "Render static. No counting up, no settling." },
  { name: "Sheet / panel", rule: "May crossfade or slide in over 150–200ms. This is the only motion." },
  { name: "Reduced motion", rule: "prefers-reduced-motion removes the sheet/panel entrance too." },
];

const forbidden = {
  visual: [
    "Colored left bars or edge strips on cards",
    "Dark backgrounds",
    "Dots as progress or status indicators",
    "Glows, neon, text gradients, frosted glass, sci-fi brackets",
    "Saturated color fills on cards",
    "Confetti or decorative gradients (only the whisper card gradient and the dot-grid canvas are allowed)",
  ],
  wording: [
    "Technical jargon of any kind",
    "Percentages or fake progress bars",
    "File paths and IDs",
    "Placeholder filler text",
  ],
};

const cssVariables = Object.fromEntries(Object.entries(colors).map(([k, v]) => [`--os-${k}`, v]));

module.exports = { colors, effects, radius, track, fonts, swatches, typeScale, motion, forbidden, cssVariables };
